import { useContext, useEffect } from 'react'
import { DateTime } from 'luxon'

import { formatDate, matchFilterBetweenDates } from '@domain-group/javascript-utils'

import { useSetState } from '@domain-group/react-hooks'

import PackContext from '@contexts/packContext'

import usePublicationEditions from '@hooks/usePublicationEditions'

import PageFilterModule from '@components/PageFilterModule'

import { PublicationEditionModel, StatusModel } from '@models/types'

const currentDateTime = DateTime.now()
const today = currentDateTime.toFormat('yyyy-MM-dd')
const nextWeek = currentDateTime.plus({ days: 6 }).toFormat('yyyy-MM-dd')
const oneMonthAgo = currentDateTime.minus({ months: 1 }).toFormat('yyyy-MM-dd')

type Strings = {
  stringLabel?: string,
}

type BuildPageFiltersParams = {
  allowedFilters: string[],
  publicationEditions: PublicationEditionModel[],
  publicationId?: number,
  setState: (payload: object) => void,
  statuses: StatusModel[],
  strings?: Strings,
}

const buildPageFilterFields = (params: BuildPageFiltersParams) => {
  const {
    allowedFilters, publicationEditions, publicationId, setState, statuses, strings,
  } = params

  const { stringLabel } = strings || {}

  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      hidden: !allowedFilters.includes('string'),
      key: 'string',
      label: stringLabel || 'Filter by Campaign Address',
    },
    {
      callbacks: {
        onChange: (id?: number) => setState({ publicationId: id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'publications',
      },
      hidden: !allowedFilters.includes('publication_id'),
      key: 'publication_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: publicationId ? 'All' : 'Select Publication',
        inputComponent: 'select',
        style,
      },
      dependentKey: 'publication_id',
      hidden: !allowedFilters.includes('publication_edition_id'),
      key: 'publication_edition_id',
      label: 'Edition',
      values: publicationEditions.map((publicationEdition) => {
        const { id, publication_date, title } = publicationEdition
        const publicationDate = formatDate(publication_date, 'dd/MM/yyyy')
        const isNextEdition = matchFilterBetweenDates(publicationDate, today, nextWeek)

        return {
          key: id,
          label: isNextEdition ? `${title} (Next Edition)` : title,
        }
      }),
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
      },
      hidden: !allowedFilters.includes('brand_id'),
      key: 'brand_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      hidden: !allowedFilters.includes('status_id'),
      key: 'status_id',
      label: 'Status',
      values: statuses.map(status => ({
        key: status.id,
        label: status.title,
      })),
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      hidden: !allowedFilters.includes('status_key'),
      key: 'status_key',
      label: 'Status',
      values: statuses.map(status => ({
        key: status.key,
        label: status.title,
      })),
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      hidden: !allowedFilters.includes('booking_status'),
      key: 'booking_status',
      label: 'Status',
      values: statuses.map(status => ({
        key: status.key,
        label: status.title,
      })),
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All Statuses',
        inputComponent: 'select',
        style,
      },
      hidden: !allowedFilters.includes('preflight_status'),
      key: 'preflight_status',
      label: 'Status',
      values: statuses.map(status => ({
        key: status.key,
        label: status.title,
      })),
    },
  ]
}

interface NewsDeskFiltersProps {
  allowedFilters: string[],
  pageDefaultFilters?: {
    preflight_status?: string,
  },
  pageFilters: {
    brand_id?: number,
    publication_edition_id?: number,
    publication_id?: number,
    status_id?: number,
    string?: string,
  },
  statuses: StatusModel[],
  strings?: Strings,
}

const defaultState = {
  publicationId: null as null | number,
  publicationEditionId: null as null | number,
}

const NewsDeskFilters = (props: NewsDeskFiltersProps) => {
  const {
    allowedFilters, pageDefaultFilters, pageFilters, statuses, strings,
  } = props

  const { publication_edition_id } = pageFilters

  const [state, setState] = useSetState(defaultState)
  const { publicationId, publicationEditionId } = state

  const packContext = useContext(PackContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = packContext

  const selectedPublicationId = publicationId || pageFilters.publication_id

  const publicationEditionsPayload = usePublicationEditions({
    filters: { min_publication_date: oneMonthAgo, publication_id: selectedPublicationId },
    performHttpRequests: !!selectedPublicationId,
  })

  const { filteredPublicationEditions } = publicationEditionsPayload

  const filterFieldParams = {
    allowedFilters,
    publicationEditions: selectedPublicationId ? filteredPublicationEditions : [],
    publicationId,
    setState,
    statuses,
    strings,
  }

  useEffect(() => {
    // publication_edition_id has a dependant key so is cleared whenever publication_id is set
    const timer = setTimeout(() => {
      setState({ publicationEditionId: publication_edition_id })
    }, 200)

    return () => clearTimeout(timer)
  }, [publication_edition_id])

  return (
    <PageFilterModule
      callbacks={{
        resetFilters: (resetFn?: () => void) => {
          setState({ publicationId: null })
          resetFilters(resetFn)
        },
        updateFilters,
      }}
      defaultFilters={{
        ...defaultFilters,
        ...pageDefaultFilters,
      }}
      externalFilters={{
        publication_id: selectedPublicationId,
        publication_edition_id: Number(publicationEditionId),
      }}
      // @ts-expect-error TS2322
      filterFields={buildPageFilterFields(filterFieldParams)}
    />
  )
}

export default NewsDeskFilters
